import type { ResolveError, Widget, WidgetTemplate } from "view/dashboard/types/widget";
import type { RegisteredSource, SourceRegistry } from "view/dashboard/widgets/sources";
import type { RegisteredVisual, ResolvedVisual, VisualId, VisualRegistry } from "view/dashboard/widgets/visuals";

// Builds a widget from the template and the two registries
export const buildWidget = (
	template: WidgetTemplate,
	sources: SourceRegistry,
	visuals: VisualRegistry,
): { ok: true; widget: Widget } | ResolveError => {
	const source = sources[template.source];
	if (!source) {
		return { ok: false, reason: "unknown-source" };
	}

	const visual = visuals[template.visual];
	if (!visual) {
		return { ok: false, reason: "unknown-visual" };
	}

	if (source.valueType !== visual.valueType) {
		return { ok: false, reason: "kind-mismatch" };
	}

	const sourceConfig = source.configSchema.safeParse(template.config.source);
	if (!sourceConfig.success) {
		return { ok: false, reason: "bad-config", issues: sourceConfig.error };
	}

	const visualConfig = visual.configSchema.safeParse(template.config.visual);
	if (!visualConfig.success) {
		return { ok: false, reason: "bad-config", issues: visualConfig.error };
	}

	return {
		ok: true,
		widget: {
			template,
			source,
			visual: visual as unknown as ResolvedVisual,
			sourceConfig: sourceConfig.data,
			visualConfig: visualConfig.data,
		},
	};
};

export const compatibleVisuals = (source: RegisteredSource, visuals: VisualRegistry) =>
	(Object.entries(visuals) as Array<[ VisualId, RegisteredVisual ]>)
		.filter(([ , visual ]) => visual.valueType === source.valueType);
